/**
 * Macro #23 — Rewards panel (owner-facing, inside Profile).
 *
 * The complete unified Rewards experience: Pending Funding / Available /
 * Withdrawal Pending / Withdrawn / Expired balances; reward history with
 * Referral + Neighbor provenance; withdrawal controls (min 350 USDC, 2% service
 * fee, network gas shown SEPARATELY, estimated net, active expiry deadline);
 * Withdraw All; Request Payout. Every value is backend-authoritative — no fake
 * earnings, no fake Vault liquidity.
 */
import { useCallback, useEffect, useState } from "react";
import { formatMicrousdc, toBigInt } from "../../lib/money";
import {
  parseUsdcToMicro,
  RewardsError,
  rewardsApi,
  serviceFeeMicro,
  type RewardRecord,
  type RewardsSelf,
  type WithdrawalRecord,
} from "../../lib/rewards-api";
import { InfluenceCard } from "./InfluenceCard";
import { Card, StatTile } from "./primitives";
import { useI18n } from "../../i18n";

type TFn = (p: string, v?: Record<string, string | number>) => string;

const fmt = (n: number) => formatMicrousdc(toBigInt(n));
const clampNonNeg = (v: bigint) => (v < 0n ? 0n : v);

function ErrorCodeMessage(t: TFn, code: string, message: string): string {
  switch (code) {
    case "WITHDRAWAL_BELOW_MINIMUM": return t("profile.rewards.errors.belowMin");
    case "WITHDRAWAL_EXCEEDS_AVAILABLE": return t("profile.rewards.errors.exceedsAvailable");
    case "INVALID_AMOUNT": return t("profile.rewards.errors.invalidAmount");
    case "UNAUTHENTICATED": return t("profile.rewards.errors.unauthenticated");
    default: return message || t("profile.rewards.errors.failed");
  }
}

export function RewardsPanel() {
  const { t } = useI18n();
  const [state, setState] = useState<"loading" | "auth" | "error" | "ok">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<RewardsSelf | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setState("loading"); setErr(null);
    try {
      const d = await rewardsApi.self(signal);
      setData(d); setState("ok");
    } catch (e) {
      if (e instanceof RewardsError && e.status === 401) { setState("auth"); return; }
      setErr(e instanceof Error ? e.message : t("profile.rewards.failedToLoad")); setState("error");
    }
  }, [t]);

  useEffect(() => { const c = new AbortController(); void load(c.signal); return () => c.abort(); }, [load]);

  if (state === "loading") return <div data-testid="rewards-loading" className="p-8 text-center font-mono text-sm text-white/50">{t("profile.rewards.loading")}</div>;
  if (state === "auth") return (
    <div data-testid="rewards-auth-required" className="mx-auto max-w-md p-8 text-center">
      <p className="mb-4 text-sm text-white/60">{t("profile.rewards.auth.note")}</p>
      <a href="#auth" data-testid="rewards-connect-link" className="inline-flex rounded-full border border-teal-300/40 bg-teal-400/10 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-teal-200 no-underline">{t("profile.rewards.auth.cta")}</a>
    </div>
  );
  if (state === "error" || !data) return (
    <div data-testid="rewards-error" className="mx-auto max-w-md p-8 text-center text-red-300">
      <p className="mb-4">{err || t("profile.rewards.unavailable")}</p>
      <button onClick={() => void load()} className="rounded-full border border-white/20 px-4 py-2 text-sm text-white/80">{t("profile.rewards.retry")}</button>
    </div>
  );

  return (
    <div data-testid="rewards-panel" className="space-y-6">
      <BalanceGrid data={data} />
      <WithdrawCard data={data} onDone={() => void load()} />
      <RequestPayoutCard available={data.available_micro} minMicro={data.min_withdrawal_micro} />
      <InfluenceCard influence={data.influence} />
      <RewardHistory items={data.history} />
      <WithdrawalHistory items={data.withdrawals} />
    </div>
  );
}

function BalanceGrid({ data }: { data: RewardsSelf }) {
  const { t } = useI18n();
  return (
    <Card title={t("profile.rewards.balance.title")} testId="rewards-balance">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatTile label={t("profile.rewards.balance.available")} value={fmt(data.available_micro)} testId="reward-available" tint="text-teal-200" />
        <StatTile label={t("profile.rewards.balance.pendingFunding")} value={fmt(data.pending_funding_micro)} testId="reward-pending-funding" tint="text-amber-200" sub={t("profile.rewards.balance.awaitingVault")} />
        <StatTile label={t("profile.rewards.balance.withdrawalPending")} value={fmt(data.withdrawal_pending_micro)} testId="reward-withdrawal-pending" />
        <StatTile label={t("profile.rewards.balance.withdrawn")} value={fmt(data.withdrawn_gross_micro)} testId="reward-withdrawn" />
        <StatTile label={t("profile.rewards.balance.expired")} value={fmt(data.expired_micro)} testId="reward-expired" tint="text-white/50" />
        <StatTile
          label={t("profile.rewards.balance.expiryDeadline")}
          testId="reward-expiry-deadline"
          value={data.timer_deadline ? new Date(data.timer_deadline).toLocaleDateString() : "—"}
          tint="text-white/80"
          sub={data.timer_deadline ? t("profile.rewards.balance.forfeits") : t("profile.rewards.balance.noTimer")}
        />
      </div>
    </Card>
  );
}

function WithdrawCard({ data, onDone }: { data: RewardsSelf; onDone: () => void }) {
  const { t } = useI18n();
  const [amount, setAmount] = useState("");
  const [gas, setGas] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [all, setAll] = useState(false);

  const available = toBigInt(data.available_micro);
  const min = toBigInt(data.min_withdrawal_micro);
  const grossMicro = all ? available : (parseUsdcToMicro(amount) ?? -1n);
  const gasMicro = gas.trim() === "" ? 0n : (parseUsdcToMicro(gas) ?? -1n);
  const validGas = gasMicro >= 0n;
  const validGross = grossMicro >= 0n;
  const fee = validGross ? serviceFeeMicro(grossMicro) : 0n;
  const net = validGross && validGas ? clampNonNeg(grossMicro - fee - gasMicro) : 0n;
  const belowMin = validGross && grossMicro < min;
  const exceeds = validGross && grossMicro > available;
  const canSubmit = !busy && data.withdrawal_enabled && validGross && validGas && grossMicro > 0n && !belowMin && !exceeds;

  const submit = async (forceAll = false) => {
    const useAll = forceAll || all;
    const g = useAll ? available : grossMicro;
    setBusy(true); setError(null); setOk(null);
    try {
      const res = useAll
        ? await rewardsApi.withdrawAll(gasMicro > 0n ? Number(gasMicro) : undefined)
        : await rewardsApi.withdraw(Number(g), gasMicro > 0n ? Number(gasMicro) : undefined);
      setOk(t("profile.rewards.withdraw.success", { net: fmt(res.net_micro), fee: fmt(res.fee_micro), gas: fmt(res.estimated_gas_micro) }));
      setAmount(""); setGas(""); setAll(false);
      onDone();
    } catch (e) {
      if (e instanceof RewardsError) setError(ErrorCodeMessage(t, e.code, e.message));
      else setError(e instanceof Error ? e.message : t("profile.rewards.errors.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t("profile.rewards.withdraw.title")} testId="withdraw-card">
      {!data.withdrawal_enabled ? (
        <p data-testid="withdraw-disabled-note" className="text-sm text-white/55">
          {t("profile.rewards.withdraw.disabledNote", { min: fmt(data.min_withdrawal_micro), available: fmt(data.available_micro) })}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase text-white/50">{t("profile.rewards.withdraw.amountLabel")}</span>
              <input
                data-testid="withdraw-amount-input"
                inputMode="decimal"
                value={all ? formatMicrousdc(available, { symbol: "" }).trim() : amount}
                disabled={all}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="≥ 350"
                className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white outline-none focus:border-teal-300/40 disabled:opacity-60"
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase text-white/50">{t("profile.rewards.withdraw.gasLabel")}</span>
              <input
                data-testid="withdraw-gas-input"
                inputMode="decimal"
                value={gas}
                onChange={(e) => setGas(e.target.value)}
                placeholder="0"
                className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white outline-none focus:border-teal-300/40"
              />
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm text-white/70">
            <input data-testid="withdraw-all-toggle" type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} className="accent-teal-400" />
            {t("profile.rewards.withdraw.allToggle", { available: fmt(data.available_micro) })}
          </label>

          <div className="grid grid-cols-3 gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div>
              <div className="font-mono text-[10px] uppercase text-white/45">{t("profile.rewards.withdraw.serviceFee")}</div>
              <div className="text-sm tabular-nums text-white/80" data-testid="withdraw-fee-estimate">{validGross ? fmt(Number(fee)) : "—"}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase text-white/45">{t("profile.rewards.withdraw.gasSeparate")}</div>
              <div className="text-sm tabular-nums text-white/80" data-testid="withdraw-gas-estimate">{validGas ? fmt(Number(gasMicro)) : "—"}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase text-white/45">{t("profile.rewards.withdraw.estimatedNet")}</div>
              <div className="text-sm tabular-nums text-teal-200" data-testid="withdraw-net-estimate">{validGross && validGas ? fmt(Number(net)) : "—"}</div>
            </div>
          </div>

          <p data-testid="withdraw-min-note" className="text-[11px] text-white/40">{t("profile.rewards.withdraw.minNote", { min: fmt(data.min_withdrawal_micro) })}</p>

          {belowMin && <p data-testid="withdraw-below-min" className="text-xs text-amber-300">{t("profile.rewards.withdraw.belowMin")}</p>}
          {exceeds && <p data-testid="withdraw-exceeds" className="text-xs text-amber-300">{t("profile.rewards.withdraw.exceeds")}</p>}

          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="withdraw-submit-btn" disabled={!canSubmit} onClick={() => void submit()} className="rounded-full border border-teal-300/40 bg-teal-400/10 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-teal-200 disabled:opacity-40">
              {busy ? t("profile.rewards.withdraw.submitting") : t("profile.rewards.withdraw.submit")}
            </button>
            <button data-testid="withdraw-all-btn" disabled={busy || !data.withdrawal_enabled} onClick={() => void submit(true)} className="rounded-full border border-white/20 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/75 disabled:opacity-40">
              {t("profile.rewards.withdraw.all")}
            </button>
            {error && <span data-testid="withdraw-error" className="text-xs text-red-300">{error}</span>}
            {ok && <span data-testid="withdraw-success" className="text-xs text-teal-300">{ok}</span>}
          </div>
        </div>
      )}
    </Card>
  );
}

function RequestPayoutCard({ available, minMicro }: { available: number; minMicro: number }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const meetsMin = available >= minMicro;
  const submit = async () => {
    if (!meetsMin) return;
    setBusy(true); setMsg(null);
    try {
      const r = await rewardsApi.requestPayout();
      setMsg(r.idempotent ? t("profile.rewards.payout.alreadyOpen") : t("profile.rewards.payout.sent"));
    } catch (e) {
      setMsg(e instanceof Error ? e.message : t("profile.rewards.payout.failed"));
    } finally { setBusy(false); }
  };
  return (
    <Card title={t("profile.rewards.payout.title")} testId="request-payout-card">
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-[12rem] flex-1 text-sm text-white/60">{t("profile.rewards.payout.body", { available: fmt(available) })}</p>
        <button
          data-testid="request-payout-btn"
          disabled={busy || !meetsMin}
          title={meetsMin ? undefined : t("profile.rewards.payout.reachTitle", { min: fmt(minMicro) })}
          onClick={() => void submit()}
          className="rounded-full border border-white/20 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/75 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? t("profile.rewards.payout.sending") : t("profile.rewards.payout.cta")}
        </button>
      </div>
      {!meetsMin && (
        <p data-testid="request-payout-min-hint" className="mt-2 text-xs text-amber-200/80">
          {t("profile.rewards.payout.minHint", { min: fmt(minMicro), available: fmt(available) })}
        </p>
      )}
      {msg && <p data-testid="request-payout-result" className="mt-2 text-xs text-teal-300">{msg}</p>}
    </Card>
  );
}

function RewardHistory({ items }: { items: RewardRecord[] }) {
  const { t } = useI18n();
  return (
    <Card title={t("profile.rewards.history.title")} testId="reward-history">
      {items.length === 0 ? (
        <p data-testid="reward-history-empty" className="text-sm text-white/45">{t("profile.rewards.history.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => <RewardRow key={r.id} r={r} />)}
        </ul>
      )}
    </Card>
  );
}

function RewardRow({ r }: { r: RewardRecord }) {
  const { t } = useI18n();
  const isReferral = r.reward_type === "REFERRAL";
  const provenance = isReferral
    ? t("profile.rewards.history.referralProv", { pct: (r.basis_bps / 100).toFixed(0), gross: fmt(r.gross_acquisition_micro) })
    : r.neighbor_provenance
      ? t("profile.rewards.history.neighborProvEdge", { edge: r.neighbor_provenance.qualified_edge_length, total: r.neighbor_provenance.total_qualified_edge_length, pct: (r.basis_bps / 100).toFixed(0) })
      : t("profile.rewards.history.neighborProvPool", { pct: (r.basis_bps / 100).toFixed(0) });
  return (
    <li data-testid={`reward-item-${r.id}`} data-reward-type={r.reward_type} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="flex items-center justify-between gap-3">
        <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[9.5px] uppercase tracking-wide ${isReferral ? "border-teal-300/40 bg-teal-400/10 text-teal-200" : "border-violet-300/40 bg-violet-400/10 text-violet-200"}`}>{r.reward_type}</span>
        <span className="text-sm font-semibold tabular-nums text-white" data-testid={`reward-amount-${r.id}`}>{fmt(r.gross_reward_micro)}</span>
      </div>
      <div className="mt-1.5 text-[11px] text-white/50" data-testid={`reward-provenance-${r.id}`}>{provenance}</div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-white/40">
        <span className={`rounded px-1.5 py-0.5 font-mono uppercase ${r.funding_state === "FUNDED" ? "bg-teal-400/10 text-teal-200" : "bg-amber-400/10 text-amber-200"}`} data-testid={`reward-funding-${r.id}`}>
          {r.funding_state === "FUNDED" ? t("profile.rewards.history.funded") : t("profile.rewards.history.pendingFunding")}
        </span>
        {r.parcel_ids.length > 0 && <span>{t("profile.rewards.history.parcels", { ids: r.parcel_ids.join(", ") })}</span>}
        {r.tx_hash && <span className="truncate font-mono">{r.tx_hash.slice(0, 10)}…</span>}
      </div>
    </li>
  );
}

function WithdrawalHistory({ items }: { items: WithdrawalRecord[] }) {
  const { t } = useI18n();
  if (items.length === 0) return null;
  return (
    <Card title={t("profile.rewards.withdrawals.title")} testId="withdrawal-history">
      <ul className="space-y-2">
        {items.map((w) => (
          <li key={w.id} data-testid={`withdrawal-item-${w.id}`} data-status={w.status} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-center justify-between gap-3">
              <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[9.5px] uppercase tracking-wide ${w.status === "WITHDRAWN" ? "border-teal-300/40 bg-teal-400/10 text-teal-200" : w.status === "FAILED" ? "border-red-300/40 bg-red-400/10 text-red-200" : "border-amber-300/40 bg-amber-400/10 text-amber-200"}`}>
                {w.status === "WITHDRAWAL_PENDING" ? t("profile.rewards.withdrawals.pending") : w.status === "WITHDRAWN" ? t("profile.rewards.withdrawals.withdrawn") : t("profile.rewards.withdrawals.failed")}
              </span>
              <span className="text-sm font-semibold tabular-nums text-white">{t("profile.rewards.withdrawals.net", { net: fmt(w.net_micro) })}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-2 text-[10px] text-white/40">
              <span>{t("profile.rewards.withdrawals.gross", { x: fmt(w.gross_micro) })}</span>
              <span>{t("profile.rewards.withdrawals.fee", { x: fmt(w.fee_micro) })}</span>
              <span>{t("profile.rewards.withdrawals.gas", { x: fmt(w.estimated_gas_micro) })}</span>
              {w.failure_reason && <span className="text-red-300/70" data-testid={`withdrawal-failure-${w.id}`}>{t("profile.rewards.withdrawals.failureReason", { reason: w.failure_reason })}</span>}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
